import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiEvents, apiSearch, apiEventByBid, apiEventByPath, ApiError, centralToEventRecord } from "../api/client";
// Shared state + fetch loaders for MainView (grid) and StudioView (player).
// Centralizing this fixed an existing bug where MainView.loadDefault retried
// on 401 via reload() but onSearch did not — the inconsistency surfaced as
// "search just shows nothing after the cookie expires" until you refreshed.
//
// fetchByBID is the deep-link path: hydrate any event regardless of age, even
// if it isn't in the most-recent 100. Used by StudioView when ?ev= points at
// an event missing from the current load.
//
// Stale-response guard (Codex /review finding):
// Without sequencing, a slow apiEvents("rc54") followed by a fast
// apiEvents("cs256") (e.g., user clicks the customer dropdown twice) could
// resolve in the wrong order — the rc54 response lands AFTER the cs256
// response and overwrites the UI with stale rc54 rows. We use both:
//   1. AbortController per call: aborts the network request so we don't
//      waste bandwidth on doomed responses.
//   2. Generation token: increments on every new call. On resolve, we
//      drop the result if our token is no longer current (handles cases
//      where abort raced with response or the request had already begun
//      streaming).
// On unmount, the latest controller is aborted to prevent setState on
// a torn-down component.
export function summarize(range) {
    const bits = [];
    if (range.q)
        bits.push(`"${range.q}"`);
    const fmt = (iso) => iso ? new Date(iso).toLocaleString() : null;
    const since = fmt(range.since);
    const until = fmt(range.until);
    if (since && until)
        bits.push(`${since} → ${until}`);
    else if (since)
        bits.push(`since ${since}`);
    else if (until)
        bits.push(`through ${until}`);
    return bits.join(" · ");
}
function isAbort(e) {
    return e instanceof DOMException && e.name === "AbortError";
}
export function useEventsController() {
    const { selectedCustomer, reload } = useAuth();
    const [events, setEvents] = useState([]);
    const [unreachable, setUnreachable] = useState([]);
    // ADR-116: central search reports an explicit degraded state instead of an
    // empty result list. Surfaced beside `unreachable`.
    const [degraded, setDegraded] = useState([]);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchedFor, setSearchedFor] = useState("");
    // Stale-response guard. Increment on every new list-fetching call.
    const listGen = useRef(0);
    const listAbort = useRef(null);
    // fetchByBID has its own sequencing — a deep-link hydrate in flight when
    // the user clicks a different event shouldn't write the prior bID into
    // selected. Separate ref so it doesn't conflict with list fetches.
    const bidGen = useRef(0);
    const bidAbort = useRef(null);
    // Abort everything on unmount so async resolutions don't setState into
    // a torn-down component.
    useEffect(() => {
        return () => {
            listAbort.current?.abort();
            bidAbort.current?.abort();
        };
    }, []);
    const handle401 = useCallback((e) => {
        if (e instanceof ApiError && e.status === 401) {
            reload();
            return true;
        }
        return false;
    }, [reload]);
    const newListRequest = useCallback(() => {
        listAbort.current?.abort();
        const ctrl = new AbortController();
        listAbort.current = ctrl;
        listGen.current += 1;
        return { signal: ctrl.signal, gen: listGen.current };
    }, []);
    const loadDefault = useCallback(async (includeEmpty = false, bCameras) => {
        const { signal, gen } = newListRequest();
        setLoading(true);
        setError(null);
        setSearchedFor("");
        try {
            const r = await apiEvents(selectedCustomer ?? undefined, undefined, undefined, 100, includeEmpty, signal, bCameras);
            if (gen !== listGen.current)
                return; // stale response; a newer call superseded us
            setEvents(r.events);
            setUnreachable(r.unreachable);
            setDegraded([]);
        }
        catch (e) {
            if (isAbort(e))
                return; // expected cancel
            if (gen !== listGen.current)
                return;
            if (!handle401(e))
                setError(String(e));
        }
        finally {
            if (gen === listGen.current)
                setLoading(false);
        }
    }, [selectedCustomer, handle401, newListRequest]);
    const search = useCallback(async (range) => {
        const hasAny = range.q.length > 0 || !!range.since || !!range.until
            || (range.bCameras && range.bCameras.length > 0);
        if (!hasAny) {
            await loadDefault(!!range.includeEmpty);
            return;
        }
        const { signal, gen } = newListRequest();
        setLoading(true);
        setError(null);
        setSearchedFor(summarize(range));
        try {
            if (range.q.length > 0) {
                const r = await apiSearch(range.q, selectedCustomer ?? undefined, range.since, range.until, undefined, signal, range.bCameras);
                if (gen !== listGen.current)
                    return;
                const central = (r.central?.results ?? []).map(centralToEventRecord);
                setEvents([...central, ...r.results]);
                setUnreachable(r.unreachable);
                setDegraded(r.central?.degraded ?? []);
            }
            else {
                const r = await apiEvents(selectedCustomer ?? undefined, range.since, range.until, 100, !!range.includeEmpty, signal, range.bCameras);
                if (gen !== listGen.current)
                    return;
                setEvents(r.events);
                setUnreachable(r.unreachable);
                setDegraded([]);
            }
        }
        catch (e) {
            if (isAbort(e))
                return;
            if (gen !== listGen.current)
                return;
            if (!handle401(e))
                setError(String(e));
        }
        finally {
            if (gen === listGen.current)
                setLoading(false);
        }
    }, [selectedCustomer, loadDefault, handle401, newListRequest]);
    // Single-event hydrate. Used when a deep-link's target isn't in the
    // chronological load (e.g. last-month event from an email). `load` is the
    // API call (by bID or by clip path); the abort/generation/prepend/select
    // wiring is identical for both, so it lives here once.
    const hydrateOne = useCallback(async (load) => {
        bidAbort.current?.abort();
        const ctrl = new AbortController();
        bidAbort.current = ctrl;
        bidGen.current += 1;
        const gen = bidGen.current;
        try {
            const ev = await load(ctrl.signal);
            if (gen !== bidGen.current)
                return null;
            // Compound dedupe key: bIDs are per-shard (each customer has its own
            // sequence), so two events from different customers can share a bID.
            // Without (customer, bID) match the prepend would be skipped when an
            // unrelated customer has an event with the same numeric bID still in
            // the list from before a customer switch.
            setEvents(prev => prev.some(e => e.bID === ev.bID && e.customer === ev.customer)
                ? prev
                : [ev, ...prev]);
            setSelected(ev);
            return ev;
        }
        catch (e) {
            if (isAbort(e))
                return null;
            if (handle401(e))
                return null;
            // 404 / 403 / network — caller decides how to surface (e.g. "Event not found")
            throw e;
        }
    }, [handle401]);
    // Legacy numeric deep-link (?ev=<bID>). Kept for any in-flight bID links.
    const fetchByBID = useCallback((customer, bID) => hydrateOne(signal => apiEventByBid(customer, bID, signal)), [hydrateOne]);
    // Durable deep-link (?evpath=<base64url(sSourcePath)>). Resolves by clip
    // path, so it survives GC delete + re-ingest that reassigns bIDs.
    const fetchByPath = useCallback((customer, key) => hydrateOne(signal => apiEventByPath(customer, key, signal)), [hydrateOne]);
    // Camera-filter dropdown options. Sourced from observed events
    // rather than a /cameras endpoint, so the pilot needs zero extra
    // backend round-trips.
    //
    // **bCamera is per-shard.** dtech.Camera.bID is TINYINT UNSIGNED
    // scoped to one NVR — cs256 cam1 = "Front Door" and cs2427 cam1 =
    // "Loading Dock" share bID=1. Deduping by bID alone (across shards)
    // collapses different cameras into one checkbox with whichever
    // label landed first; same lesson as
    // pitfall_per_shard_bid_treated_as_global.md (fix a25518d15). We
    // scope to `selectedCustomer` and show no filter when the admin
    // view is fanned across customers.
    //
    // **The list is sticky.** Naive `useMemo([events])` would shrink the
    // option set after a filtered or zero-result search — selecting
    // "Front Door" returns only Front Door events, the dropdown
    // collapses to 1 option, SearchBox hides the fieldset (which
    // requires >=2 options), but the local `selectedCams` state still
    // holds the filter. The user can no longer see or unselect it, and
    // every subsequent query stays mysteriously broken. Codex
    // structured review caught this in the pilot review.
    //
    // Fix: maintain a sticky set per customer. Cameras are added when
    // observed; the set is reset only on customer switch. Filtered or
    // empty result sets never shrink the option list.
    const [cameraSetByCustomer, setCameraSetByCustomer] = useState({});
    useEffect(() => {
        if (!selectedCustomer)
            return;
        setCameraSetByCustomer(prev => {
            const next = { ...prev };
            const current = new Map(next[selectedCustomer] ?? []);
            let mutated = false;
            for (const ev of events) {
                if (typeof ev.bCamera !== "number")
                    continue;
                if (ev.customer && ev.customer !== selectedCustomer)
                    continue;
                const existing = current.get(ev.bCamera);
                if (existing === undefined) {
                    current.set(ev.bCamera, ev.sCameraName ?? null);
                    mutated = true;
                }
                else if (existing === null && ev.sCameraName) {
                    // Upgrade a previously-unnamed bID once a named event lands.
                    current.set(ev.bCamera, ev.sCameraName);
                    mutated = true;
                }
            }
            if (!mutated)
                return prev;
            next[selectedCustomer] = current;
            return next;
        });
    }, [events, selectedCustomer]);
    const availableCameras = useMemo(() => {
        if (!selectedCustomer)
            return [];
        const map = cameraSetByCustomer[selectedCustomer];
        if (!map)
            return [];
        const out = [];
        for (const [bID, name] of map.entries())
            out.push({ bID, name });
        out.sort((a, b) => a.bID - b.bID);
        return out;
    }, [cameraSetByCustomer, selectedCustomer]);
    return {
        events, unreachable, degraded, selected, setSelected,
        loading, error, searchedFor,
        loadDefault, search, fetchByBID, fetchByPath,
        setSearchedFor,
        availableCameras,
    };
}
